import styled from 'styled-components'
import { VDivider } from '@/features/shared/components/Dividers'
import { HeaderLogo } from '@/features/shared/components/Headers/HeaderLogo'
import { NextDeliveryWindow } from '@/features/shop/components/NextDeliveryWindow'
import { useGuestParams } from '@/features/account/services/GuestUser/hooks'
import { Image } from '@/features/shared/elements/Image/NextImage'
import { useUserStoreImage } from '@/features/account/services/User/hooks'
import { GridLayoutContainer } from '@/features/shared/styles/layout'
import { Body } from '@shipt/design-system-typography'
import { Column, Row } from '@shipt/design-system-layouts'
import { Button, IconButton, TextButton } from '@shipt/design-system-buttons'
import { screenSizes } from '@shipt/design-system-themes'
import { AddressIcon, ChevronDownIcon, MenuIcon } from '@shipt/design-system-icons'
import { DummyCartIcon } from '@/features/shared/components/Headers/Header/DummyCartIcon'
import { useScrollWithThreshold } from '@/features/shared/components/Headers/useScrollWithThreshold'
import { BasicShadow } from '@/features/shared/styles/effects'
import { isOnServer } from '@shared/constants/util'
import { ZipConfirmationPopover } from '@/features/account/components/ZipConfirmationPopover'
import { HEADER_Z_INDEX } from '@/features/shared/constants/global'
import { PDP_ATC_CLICKED } from '@/features/shop/components/ProductDetail/constants'
import { TransparentButton } from '@/features/shared/elements/Buttons'
import { useOpenGuestAddressModalForPLA } from '@/features/shop/components/ProductDetail/PLA/hooks'
import { type StoreParams } from '@/features/shared/utils/dataFetching/storeParams'
import { type ShoppingStoreDetails } from '@/features/shop/services/ShoppingStore/types'
import { useHandleDialog } from '@/features/shared/state/Dialog/useHandleDialog'
import { DynamicHamburgerMenuDrawer } from '@/features/shop/components/Drawer/HamburgerMenuDrawer/DynamicHamburgerMenuDrawer'
import { useSearchQueryParams } from '@/features/shared/hooks/search'

const icons = {
  leadingIcon: AddressIcon,
  trailingIcon: ChevronDownIcon,
} as const
const displaySublocation = 'zip_only_header'

export type PLAGuestWithZipOnlyHeaderOnSubmit = (params: {
  store: ShoppingStoreDetails
  storeParams: StoreParams
}) => Promise<unknown>
type Props = { onSubmit: PLAGuestWithZipOnlyHeaderOnSubmit }

export const PLAGuestWithZipOnlyHeader = ({ onSubmit }: Props) => {
  const { zip_code = '' } = useGuestParams()?.guestAddress ?? {}
  const isPastScrollThreshold = useScrollWithThreshold(
    !isOnServer() && window.scrollY > 90 ? 90 : 126,
  )
  const { openDialog } = useHandleDialog()
  const handleHamburgerOnClick = () =>
    openDialog(DynamicHamburgerMenuDrawer, { isGlobalMenu: false })

  const MenuButton = (
    <IconButton
      icon={MenuIcon}
      size="md"
      variant="ghost"
      onClick={handleHamburgerOnClick}
      aria-label="Main menu"
    />
  )

  return (
    <Header data-past-scroll={isPastScrollThreshold}>
      <TabletLayoutContainer>
        <Row align="center" justify="space-between">
          <Row align="center" justify="center" spacing="sm">
            {MenuButton}
            <HeaderContentButton />
          </Row>
          <Row align="center" spacing="lg">
            <NextDeliveryWindowButton />
            <ZipConfirmationPopover onSubmit={onSubmit} displaySublocation={displaySublocation}>
              {(props) => (
                <Button size="sm" variant="tertiary" {...props} {...icons}>
                  {zip_code}
                </Button>
              )}
            </ZipConfirmationPopover>
            <DummyCartIconButton />
          </Row>
        </Row>
      </TabletLayoutContainer>
      <MobileColumn spacing="md" justify="center" isPastScrollThreshold={isPastScrollThreshold}>
        <Row align="center" justify="space-between" spacing="sm">
          {MenuButton}
          {isPastScrollThreshold ? (
            <MobileAddressButton onSubmit={onSubmit} />
          ) : (
            <HeaderContentButton />
          )}
          <DummyCartIconButton />
        </Row>
        {!isPastScrollThreshold && <MobileAddressButton onSubmit={onSubmit} />}
      </MobileColumn>
    </Header>
  )
}

const OpenGuestAddressModalButton = ({ children, ...rest }: { children: React.ReactNode }) => {
  const openGuestAddressModal = useOpenGuestAddressModalForPLA()
  return (
    <TransparentButton onClick={openGuestAddressModal} {...rest}>
      {children}
    </TransparentButton>
  )
}

const DummyCartIconButton = () => {
  const searchParams = useSearchQueryParams()
  return (
    <OpenGuestAddressModalButton aria-label="Select to add an address to view cart">
      <DummyCartIcon showIndicator={Boolean(searchParams[PDP_ATC_CLICKED])} />
    </OpenGuestAddressModalButton>
  )
}

const MobileAddressButton = ({ onSubmit }: { onSubmit: PLAGuestWithZipOnlyHeaderOnSubmit }) => {
  const { zip_code = '' } = useGuestParams()?.guestAddress ?? {}

  return (
    <ZipConfirmationPopover onSubmit={onSubmit} displaySublocation={displaySublocation}>
      {(props) => (
        <MobileTextButton {...props} {...icons}>
          <Body size="md">{zip_code}</Body>
        </MobileTextButton>
      )}
    </ZipConfirmationPopover>
  )
}

const HeaderContentButton = () => {
  const { image: storeImage } = useUserStoreImage()

  return (
    <OpenGuestAddressModalButton aria-label="Select to add an address">
      <Row align="center" justify="center">
        <HeaderLogo />
        <Divider />
        <StoreImageWrapper>
          {!!storeImage && <Image alt="" src={storeImage} width={40} height={40} />}
        </StoreImageWrapper>
      </Row>
    </OpenGuestAddressModalButton>
  )
}

const Header = styled.header<{ 'data-past-scroll': boolean }>`
  top: 0;
  position: sticky;
  z-index: ${HEADER_Z_INDEX};
  background-color: ${({ theme }) => theme.white};

  @media ${screenSizes.max_mobile} {
    background-color: ${({ theme }) => theme.color.background.base.neutral.subtle_100};

    &[data-past-scroll='true'] {
      background-color: ${({ theme }) => theme.white};
      ${BasicShadow}
    }
  }
`

const TabletLayoutContainer = styled(GridLayoutContainer)`
  border-bottom: 2px solid
    ${({ theme }) => theme.color.border.base.neutral.subtle};

  & > * {
    height: 4rem;
  }

  @media ${screenSizes.max_mobile} {
    display: none;
  }
`

const Divider = styled(VDivider)`
  height: 2rem;
  width: 0.125rem;
  margin: 0 1rem;
`

const NextDeliveryWindowButton = styled(NextDeliveryWindow)`
  height: 2rem;
  padding: 0.375rem 0.875rem;
`

const MobileColumn = styled(Column)<{ isPastScrollThreshold: boolean }>`
  height: 6.25rem;
  padding: 0.75rem 1rem 0.5rem;
  overflow: hidden; /* ensures content beyond the max-height is not visible */
  transition: max-height 0.1s ease-in-out;
  max-height: ${({ isPastScrollThreshold }) => (isPastScrollThreshold ? '4rem' : '6.25rem')};

  @media ${screenSizes.tablet} {
    display: none;
  }
`

const StoreImageWrapper = styled(Row)`
  width: 40px;
  height: 40px;
  border-radius: 100%;
`

const MobileTextButton = styled(TextButton)`
  margin: 0 auto;
`
